import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';

/**
 * REPORTING-CENTER.1 — help modal for the venue Reports page, following the
 * InventoryHelpModal.tsx idiom (info icon → glass dialog).
 */

interface RowProps {
  label: string;
  children: React.ReactNode;
}

const FieldRow: React.FC<RowProps> = ({ label, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 190, flexShrink: 0, color: theme.palette.primary.main }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const ReportsHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark ? 'rgba(22, 22, 35, 0.90)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReportsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Reports Guide
            </Typography>
            <Typography variant="caption" color="text.secondary">
              What lives on this page
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          This page collects the venue&apos;s vetted reports. Each card is a shortcut — nothing
          here changes data, and every report is read-only.
        </Typography>

        <Divider />

        <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
          <FieldRow label="Movements & Stock on Hand">
            Pick any date range to see opening stock, every movement, and closing stock per item.
            Export: XLSX
          </FieldRow>
          <FieldRow label="Transaction Register">
            A line-by-line ledger of every movement with In/Out columns and full filtering. Pick a
            single item for its Stock Card with a running balance. Export: XLSX &amp; CSV
          </FieldRow>
          <FieldRow label="Inventory Cycle Report">
            Explains the period between two posted stock counts, including the expected-vs-counted
            variance. Export: XLSX — from a posted count&apos;s page
          </FieldRow>
          <FieldRow label="Stock Counts">
            Create, enter, and post physical stock counts. Export: XLSX — blank count worksheets
          </FieldRow>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportsHelpModal;
